import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Bold,
  Essentials,
  Italic,
  Mention,
  Paragraph,
  Undo,
  Heading,
  Link,
  List,
  BlockQuote,
  Table,
  TableToolbar,
  Alignment,
  FontSize,
  FontFamily,
  FontColor,
  FontBackgroundColor,
  Underline,
  Strikethrough,
  SourceEditing,
  GeneralHtmlSupport,
  type EditorConfig,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import { Box } from '@mui/material';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
}

const RichTextEditor = ({
  value,
  onChange,
  placeholder = 'Enter content here...',
  error = false,
}: RichTextEditorProps) => {
  const editorConfig: EditorConfig = {
    licenseKey: 'GPL',

    toolbar: [
      'undo',
      'redo',
      '|',
      'sourceEditing',
      '|',
      'heading',
      '|',
      'fontSize',
      'fontFamily',
      'fontColor',
      'fontBackgroundColor',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'link',
      'insertTable',
      'blockQuote',
      '|',
      'alignment',
      '|',
      'bulletedList',
      'numberedList',
    ],

    plugins: [
      Bold,
      Essentials,
      Italic,
      Mention,
      Paragraph,
      Undo,
      Heading,
      Link,
      List,
      BlockQuote,
      Table,
      TableToolbar,
      Alignment,
      FontSize,
      FontFamily,
      FontColor,
      FontBackgroundColor,
      Underline,
      Strikethrough,
      SourceEditing,
      GeneralHtmlSupport, // 🔥 ADDED: Allows custom HTML
    ],

    // 🔥 ALLOW FULL CUSTOM HTML (div, class, styles, attributes)
    htmlSupport: {
      allow: [
        {
          name: /.*/, // allow all tags
          classes: true, // allow class=""
          attributes: true, // allow any HTML attribute
          styles: true, // allow inline styles
        },
      ],
    },

    placeholder,

    table: {
      contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
    },
  };

  // Pre-process raw HTML to protect template braces in style attributes before loading into CKEditor
  const preProcess = (html: string): string => {
    if (!html) return '';
    // Replace style="{{variable}}" with data-style-placeholder="variable"
    return html.replace(
      /style="\{\{([^}]+)\}\}"/g,
      'data-style-placeholder="$1"'
    );
  };

  // Post-process HTML from CKEditor to restore template braces
  const postProcess = (html: string): string => {
    if (!html) return '';
    // Replace data-style-placeholder="variable" back to style="{{variable}}"
    return html.replace(/data-style-placeholder="([^"]+)"/g, 'style="{{$1}}"');
  };

  return (
    <Box
      sx={{
        border: error ? '2px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
        borderRadius: '4px',
        overflow: 'hidden',
        '&:hover': {
          borderColor: error ? '#d32f2f' : 'rgba(0, 0, 0, 0.87)',
        },
        '&:focus-within': {
          borderColor: error ? '#d32f2f' : '#1976d2',
          borderWidth: '2px',
        },
        '& .ck.ck-editor': {
          width: '100%',
        },
        '& .ck-editor__editable': {
          minHeight: '400px !important',
          maxHeight: '600px',
          overflow: 'auto',
        },
        '& .ck-editor__editable_inline': {
          padding: '16px',
        },
        '& .ck.ck-toolbar': {
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <CKEditor
        editor={ClassicEditor}
        config={editorConfig}
        data={preProcess(value) || ''}
        onChange={(_event, editor) => {
          const rawData = editor.getData();
          const cleanData = postProcess(rawData);
          onChange(cleanData);
        }}
      />
    </Box>
  );
};

export default RichTextEditor;
